/* eslint-disable react/require-default-props */
import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import Spinner from '@/components/common/loading/spinner';
import {
  DESTRUCTIVE_CLSX,
  DESTRUCTIVE_GHOST_CLSX,
  GHOST_CLSX,
  GRADIENT_BLUE_CLSX,
  GRADIENT_CLSX,
  LARGE_BUTTON_CLSX,
  MEDIUM_BUTTON_CLSX,
  PRIMARY_CLSX,
  PRIMARY_LIGHT_CLSX,
  PRIMARY_RED_CLSX,
  SECONDARY_BLACK_CLSX,
  SECONDARY_CLSX,
  SECONDARY_RED_CLSX,
  SECONDARY_WHITE_CLSX,
  SMALL_BUTTON_CLSX,
  TERTIARY_CLSX,
} from '@/constants/button';

export enum ButtonVariant {
  PRIMARY = 'primary',
  PRIMARY_LIGHT = 'primary-light',
  PRIMARY_RED = 'primary-red',
  SECONDARY = 'secondary',
  SECONDARY_WHITE = 'secondary-white',
  SECONDARY_BLACK = 'secondary-black',
  SECONDARY_RED = 'secondary-red',
  TERTIARY = 'tertiary',
  GHOST = 'ghost',
  DESTRUCTIVE = 'destructive',
  DESTRUCTIVE_GHOST = 'destructive-ghost',
  GRADIENT = 'gradient',
  GRADIENT_BLUE = 'gradient-blue',
}

export enum ButtonSize {
  LARGE = 'lg',
  MEDIUM = 'md',
  SMALL = 'sm',
  XS = 'xs',
}

interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  isLoading?: boolean;
  onDisabledClick?: Function;
  type?: 'submit' | 'reset' | 'button' | undefined;
}

export const variants: Record<ButtonVariant, string> = {
  [ButtonVariant.PRIMARY_LIGHT]: PRIMARY_LIGHT_CLSX,
  [ButtonVariant.PRIMARY]: PRIMARY_CLSX,
  [ButtonVariant.PRIMARY_RED]: PRIMARY_RED_CLSX,
  [ButtonVariant.SECONDARY]: SECONDARY_CLSX,
  [ButtonVariant.SECONDARY_WHITE]: SECONDARY_WHITE_CLSX,
  [ButtonVariant.SECONDARY_BLACK]: SECONDARY_BLACK_CLSX,
  [ButtonVariant.SECONDARY_RED]: SECONDARY_RED_CLSX,
  [ButtonVariant.TERTIARY]: TERTIARY_CLSX,

  [ButtonVariant.GHOST]: GHOST_CLSX,
  [ButtonVariant.DESTRUCTIVE]: DESTRUCTIVE_CLSX,
  [ButtonVariant.DESTRUCTIVE_GHOST]: DESTRUCTIVE_GHOST_CLSX,
  [ButtonVariant.GRADIENT]: GRADIENT_CLSX,
  [ButtonVariant.GRADIENT_BLUE]: GRADIENT_BLUE_CLSX,
};

const sizes: Record<ButtonSize, string> = {
  [ButtonSize.LARGE]: LARGE_BUTTON_CLSX,
  [ButtonSize.MEDIUM]: MEDIUM_BUTTON_CLSX,
  [ButtonSize.SMALL]: SMALL_BUTTON_CLSX,
  [ButtonSize.XS]: SMALL_BUTTON_CLSX,
};

const Button = forwardRef(
  (
    {
      children,
      disabled,
      className = undefined,
      isLoading = false,
      onDisabledClick = undefined,
      size = ButtonSize.LARGE,
      type = 'button',
      variant = ButtonVariant.PRIMARY,
      ...otherButtonProps
    }: IButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <button
        ref={ref}
        className={twMerge(
          'flex font-[Roboto] transition-all',
          size && sizes[size],
          variant && variants[variant],
          disabled && 'cursor-pointer opacity-[0.3]',
          className
        )}
        disabled={(disabled && !onDisabledClick) || isLoading}
        // eslint-disable-next-line react/button-has-type
        type={type}
        {...otherButtonProps}
        onClick={(e) => {
          if (disabled) {
            onDisabledClick?.(e);
          } else {
            otherButtonProps.onClick?.(e);
          }
          e.currentTarget.blur();
        }}
      >
        {isLoading ? <Spinner isSmall={size === ButtonSize.SMALL} /> : children}
      </button>
    );
  }
);

export default Button;
